import {
  AssessmentOutlined,
  AutoAwesomeOutlined,
  BarChartOutlined,
  BusinessOutlined,
  HistoryEduOutlined,
  CurrencyRupeeOutlined,
  EventAvailableOutlined,
  GridViewOutlined,
  HomeOutlined,
  InboxOutlined,
  InsightsOutlined,
  LocalShippingOutlined,
  CategoryOutlined,
  PlaceOutlined,
  AccountBalanceOutlined,
  KayakingOutlined,
  TourOutlined,
  AltRouteOutlined,
  StorefrontOutlined,
  FactCheckOutlined,
  ListAltOutlined,
  PercentOutlined,
  ReceiptLongOutlined,
  RequestQuoteOutlined,
  SettingsOutlined,
  TrendingUpOutlined,
  TuneOutlined,
  HotelOutlined, BedOutlined,
} from '@mui/icons-material';
import type { ReactNode } from 'react';

/**
 * Which side of the business somebody is working on.
 *
 * <p>
 * A B2C workspace sells to the traveller; a B2B workspace sells through an agent. One company, two
 * workspaces, switched in the header. Entries without a `workspaces` field appear in both.
 * </p>
 */
export type Workspace = 'b2c' | 'b2b';

export interface NavItem {
  label: string;
  /** Leaf route. Omitted only when the item is a submenu parent (`children` set instead). */
  to?: string;
  icon?: ReactNode;
  perms?: string[]; // visible if user has any of these (empty = always)
  /**
   * Optional tenant feature-flag code (e.g. 'Module.Ai'). Hidden only when the tenant has that flag
   * and it is explicitly disabled; unknown/untagged codes are treated as enabled (fail-open).
   */
  feature?: string;
  /** Optional count badge. Rendered when > 0. */
  badge?: number;
  /** Optional badge colour; defaults to the primary accent. */
  badgeColor?: 'primary' | 'error' | 'warning' | 'success' | 'info';
  /** The workspaces this entry belongs to. Omitted means BOTH. */
  workspaces?: Workspace[];
  /**
   * ONE optional submenu level. Depth is capped at Section, Menu, Submenu; a `children` entry must
   * never itself define `children`.
   */
  children?: NavItem[];
}

/**
 * One entry in the sidebar, sitting under a section heading.
 */
export interface NavGroup {
  label: string;
  icon: ReactNode;
  /** Direct-link entry (no menus). Mutually exclusive with `children`. */
  to?: string;
  perms?: string[];
  /**
   * The uppercase heading this entry sits under. Entries sharing a section render together beneath one
   * heading, in the order they appear here. Omitted for entries above every heading (Dashboard).
   */
  section?: string;
  /** Shown, styled like everything else, but NOT navigable: the feature does not exist yet. */
  comingSoon?: boolean;
  /** A small chip beside the label, e.g. "New". */
  tag?: string;
  /** Live count pill, rendered only when greater than zero. */
  badge?: number;
  badgeColor?: 'primary' | 'error' | 'warning' | 'success' | 'info';
  /** The workspaces this entry belongs to. Omitted means BOTH. */
  workspaces?: Workspace[];
  children?: NavItem[];
}

/**
 * The sidebar. Every `to` is an existing route; RBAC filtering happens in the Sidebar. New modules add
 * their entries here as they are built.
 */
export const navGroups: NavGroup[] = [
  // Above every heading: Home, then the daily-work modules in the order the design lists them. A module
  // that is not built yet is shown (as designed) but goes nowhere until it ships.
  { label: 'Home', icon: <HomeOutlined />, to: '/dashboard' },
  { label: 'Approvals', to: '/approvals', icon: <FactCheckOutlined />, perms: ['Vendor.Approve', 'Hotel.Approve'] },
  { label: 'Enquiries', icon: <InboxOutlined />, comingSoon: true },
  { label: 'Quotes', icon: <RequestQuoteOutlined />, comingSoon: true },
  { label: 'Bookings', icon: <EventAvailableOutlined />, comingSoon: true },
  { label: 'Operations', icon: <LocalShippingOutlined />, comingSoon: true },
  { label: 'Accounts', icon: <CurrencyRupeeOutlined />, comingSoon: true },
  { label: 'Reports', icon: <BarChartOutlined />, comingSoon: true },

  // ---- SETUP ----
  {
    label: 'Masters',
    icon: <GridViewOutlined />,
    section: 'SETUP',
    children: [
      { label: 'Destinations', to: '/destinations', icon: <PlaceOutlined />, perms: ['City.View'], feature: 'Module.Destinations' },
      { label: 'Attractions', to: '/attractions', icon: <AccountBalanceOutlined />, perms: ['City.View'], feature: 'Module.Destinations' },
      { label: 'Activities', to: '/activities', icon: <KayakingOutlined />, perms: ['City.View'], feature: 'Module.Destinations' },
      { label: 'Sub-tours', to: '/sub-tours', icon: <TourOutlined />, perms: ['City.View'], feature: 'Module.Destinations' },
      { label: 'Travel times', to: '/travel-times', icon: <AltRouteOutlined />, perms: ['City.View'], feature: 'Module.Destinations' },
      { label: 'Vendors', to: '/vendors', icon: <StorefrontOutlined />, perms: ['Vendor.View'], feature: 'Module.Vendors' },
      { label: 'Vendor lists', to: '/vendor-lists', icon: <ListAltOutlined />, perms: ['Vendor.View'], feature: 'Module.Vendors' },
      { label: 'TDS sections', to: '/vendor-lists/tds', icon: <PercentOutlined />, perms: ['Vendor.View'], feature: 'Module.Vendors' },
      { label: 'Hotels', to: '/hotels', icon: <HotelOutlined />, perms: ['Hotel.View'], feature: 'Module.Hotels' },
      { label: 'Hotel lists', to: '/hotel-lists', icon: <BedOutlined />, perms: ['Hotel.View'], feature: 'Module.Hotels' },
      { label: 'Tour categories', to: '/tour-categories', icon: <CategoryOutlined />, perms: ['Company.View'] },
    ],
  },
  {
    label: 'Settings',
    icon: <TuneOutlined />,
    section: 'SETUP',
    children: [
      {
        label: 'Business Profile',
        to: '/business-profile',
        icon: <BusinessOutlined />,
        perms: ['Company.View', 'User.View', 'Branch.View', 'Role.View', 'Permission.View'],
      },
      { label: 'Branding, Email & WhatsApp', to: '/settings', icon: <SettingsOutlined />, perms: ['Company.View'] },
      { label: 'Vendor agreements', to: '/settings/vendor-agreements', icon: <HistoryEduOutlined />, perms: ['Vendor.View'], feature: 'Module.Vendors' },
      { label: 'AI Setup', to: '/settings/ai/configuration', icon: <TuneOutlined />, perms: ['Company.View'], feature: 'Module.Ai' },
    ],
  },
];

/** The section headings, in the order the sidebar renders them. A section with nothing visible renders nothing. */
export const NAV_SECTIONS = ['SETUP'] as const;

/**
 * The menu for one workspace: one list filtered, rather than two lists maintained. Only entries that
 * genuinely differ carry a `workspaces` field; everything without one appears in both.
 */
export function navGroupsFor(workspace: Workspace): NavGroup[] {
  const mine = <T extends { workspaces?: Workspace[] }>(entry: T) =>
    !entry.workspaces || entry.workspaces.includes(workspace);

  return navGroups
    .filter(mine)
    .map((group) => (group.children
      ? { ...group, children: group.children.filter(mine) }
      : group))
    // A parent whose every child belonged to the other workspace is a menu that opens onto nothing.
    .filter((group) => !group.children || group.children.length > 0);
}
